import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { getHourlyFakeBase } from '../../../lib/hourlyCount';
import { MOCK_WAITING_TENANTS } from '../../../mocks/tenantWaiting';
import type { WaitingTenant } from '../../../mocks/tenantWaiting';

interface TenantWaitingModalProps {
  isOpen: boolean;
  onClose: () => void;
  displayCount?: number | null;
}

function anonymizeName(name: string): string {
  if (!name) return '某**';
  return name.charAt(0) + '**';
}

function daysAgoText(days: number): string {
  if (days === 0) return '今天';
  if (days === 1) return '1天前';
  return `${days}天前`;
}

function getHourlyShuffledMocks(): WaitingTenant[] {
  const hourSeed = Math.floor(Date.now() / (1000 * 60 * 60));
  const list = [...MOCK_WAITING_TENANTS];
  for (let i = list.length - 1; i > 0; i--) {
    const seed = ((hourSeed * 1664525 + i * 1013904223) >>> 0);
    const j = seed % (i + 1);
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
}

const ROOM_STYLE: Record<string, { pill: string; dot: string }> = {
  '套房': { pill: 'bg-teal-50 text-teal-700 border-teal-200',   dot: 'bg-teal-400' },
  '1房':  { pill: 'bg-sky-50 text-sky-700 border-sky-200',      dot: 'bg-sky-400' },
  '2房':  { pill: 'bg-violet-50 text-violet-700 border-violet-200', dot: 'bg-violet-400' },
  '3房':  { pill: 'bg-orange-50 text-orange-700 border-orange-200', dot: 'bg-orange-400' },
  '4房以上': { pill: 'bg-rose-50 text-rose-700 border-rose-200',  dot: 'bg-rose-400' },
};

function getRoomStyle(room: string) {
  return ROOM_STYLE[room] ?? { pill: 'bg-stone-50 text-stone-600 border-stone-200', dot: 'bg-stone-400' };
}

export default function TenantWaitingModal({ isOpen, onClose, displayCount }: TenantWaitingModalProps) {
  const [realTenants, setRealTenants] = useState<WaitingTenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [shuffledMocks] = useState<WaitingTenant[]>(() => getHourlyShuffledMocks());

  useEffect(() => {
    if (!isOpen) return;
    const fetchReal = async () => {
      setLoading(true);
      try {
        const { data } = await supabase
          .from('tenant_match_requests')
          .select('id, contact_name, region, max_price, rooms, min_area, pet_allowed, created_at')
          .order('created_at', { ascending: false })
          .limit(50);

        if (data) {
          const converted: WaitingTenant[] = data.map((r) => {
            const created = new Date(r.created_at);
            const now = new Date();
            const diff = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
            return {
              id: r.id,
              displayName: anonymizeName(r.contact_name || ''),
              region: r.region || '全台',
              maxPrice: r.max_price || 0,
              rooms: Array.isArray(r.rooms) && r.rooms.length > 0 ? r.rooms[0] : '不限',
              minArea: r.min_area,
              petAllowed: r.pet_allowed,
              daysAgo: diff,
            };
          });
          setRealTenants(converted);
        }
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    fetchReal();
  }, [isOpen]);

  if (!isOpen) return null;

  const allTenants = [...realTenants, ...shuffledMocks];
  const totalCount = displayCount != null ? displayCount : getHourlyFakeBase();

  // 快速統計房型分布
  const roomCounts = allTenants.reduce<Record<string, number>>((acc, t) => {
    acc[t.rooms] = (acc[t.rooms] || 0) + 1;
    return acc;
  }, {});
  const topRooms = Object.entries(roomCounts).sort((a, b) => b[1] - a[1]).slice(0, 4);
  const totalForPct = allTenants.length || 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg bg-white rounded-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: '88vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-5 pt-5 pb-4 flex-shrink-0">
          {/* 裝飾圓 */}
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-teal-500/10 rounded-full pointer-events-none" />
          <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-amber-500/8 rounded-full pointer-events-none" />

          <div className="relative flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 flex items-center justify-center bg-teal-500/20 border border-teal-400/30 rounded-xl flex-shrink-0">
                <i className="ri-user-search-line text-teal-400 text-xl"></i>
              </div>
              <div>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-black text-white leading-none tabular-nums">{totalCount}</span>
                  <span className="text-base font-bold text-teal-400">位租客</span>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">正在等待符合條件的房源出現</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-lg transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-lg text-white"></i>
            </button>
          </div>

          {/* 房型分布快速統計 */}
          <div className="relative grid grid-cols-4 gap-1.5">
            {topRooms.map(([room, count]) => {
              const style = getRoomStyle(room);
              const pct = Math.round((count / totalForPct) * 100);
              return (
                <div key={room} className="bg-white/8 border border-white/10 rounded-xl px-2 py-2 text-center">
                  <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold mb-1 border ${style.pill}`}>
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${style.dot}`}></span>
                    {room}
                  </div>
                  <p className="text-sm font-black text-white leading-none">{pct}%</p>
                </div>
              );
            })}
          </div>

          {/* 即時提醒標籤 */}
          <div className="relative flex items-center gap-2 mt-3 px-3 py-1.5 bg-white/8 border border-white/10 rounded-xl">
            <span className="relative flex h-2 w-2 flex-shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
            </span>
            <span className="text-[10px] text-slate-400">以下為匿名顯示，資料僅供屋主參考，不對外公開</span>
          </div>
        </div>

        {/* ── 租客列表 ── */}
        <div className="flex-1 overflow-y-auto bg-slate-50">
          {loading && realTenants.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <i className="ri-loader-4-line animate-spin text-2xl text-teal-400"></i>
            </div>
          ) : (
            <div className="p-3 space-y-2">
              {allTenants.map((tenant, idx) => {
                const style = getRoomStyle(tenant.rooms);
                return (
                  <div
                    key={`${tenant.id}-${idx}`}
                    className="bg-white rounded-xl border border-gray-100 px-4 py-3 flex items-center gap-3"
                  >
                    {/* 彩色左邊框指示 */}
                    <div className={`w-1 h-10 rounded-full flex-shrink-0 ${style.dot}`}></div>

                    {/* 頭像 */}
                    <div className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-full flex-shrink-0">
                      <span className="text-xs font-black text-slate-600">{tenant.displayName.charAt(0)}</span>
                    </div>

                    {/* 主要資訊 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-sm font-bold text-gray-900">{tenant.displayName}</span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${style.pill}`}>
                          {tenant.rooms}
                        </span>
                        {tenant.petAllowed && (
                          <span className="text-[10px] text-teal-600 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded-full font-bold">
                            <i className="ri-bear-smile-line mr-0.5"></i>寵物
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="flex items-center gap-0.5 text-slate-500 truncate">
                          <i className="ri-map-pin-fill text-teal-500 text-xs flex-shrink-0"></i>
                          <span className="truncate">{tenant.region}</span>
                        </span>
                        {tenant.minArea && (
                          <span className="text-slate-400 flex-shrink-0">≥{tenant.minArea}坪</span>
                        )}
                      </div>
                    </div>

                    {/* 右側：價格 + 時間 */}
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="text-sm font-black text-red-500">
                        ${(tenant.maxPrice / 1000).toFixed(0)}k
                        <span className="text-xs font-normal text-gray-400">/月</span>
                      </span>
                      <span className="text-[10px] text-gray-400">{daysAgoText(tenant.daysAgo)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── 底部 CTA（完全獨立於列表外） ── */}
        <div className="flex-shrink-0 bg-white border-t-2 border-gray-100">
          {/* 上方說明文字區塊 */}
          <div className="px-5 pt-4 pb-3 bg-gradient-to-r from-teal-50 to-amber-50 border-b border-gray-100">
            <div className="flex items-start gap-2.5">
              <div className="w-7 h-7 flex items-center justify-center bg-teal-500 rounded-lg flex-shrink-0 mt-0.5">
                <i className="ri-robot-2-line text-white text-sm"></i>
              </div>
              <div>
                <p className="text-sm font-black text-gray-900">上架後 AI 立即比對 · 房源不怕沒人看</p>
                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                  以上每一位租客都填寫了明確需求，系統一旦偵測到符合條件的新房源，立即推送通知，不需要等待
                </p>
              </div>
            </div>
          </div>
          {/* 按鈕列 */}
          <div className="flex gap-2.5 px-5 py-3.5">
            <Link
              to="/publish"
              onClick={onClose}
              className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-black text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line text-base"></i>
              我是屋主，上架房源
            </Link>
            <Link
              to="/ai-match"
              onClick={onClose}
              className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-amber-400 hover:bg-amber-500 text-gray-900 rounded-xl font-black text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line text-base"></i>
              我是租客找房
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
